import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const LINKS = [
  { to: "/", label: "Início" },
  { to: "/produtos", label: "Produtos" },
  { to: "/novidades", label: "Novidades" },
  { to: "/sobre", label: "Sobre Nós" },
  { to: "/contactos", label: "Contactos" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 px-4 md:px-8 pt-4">
      <nav
        data-testid="navbar"
        className={`mx-auto max-w-6xl flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          scrolled ? "bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/40" : "bg-white/40 backdrop-blur-md border border-white/20"
        }`}
      >
        <Link to="/" data-testid="logo" className="flex items-center shrink-0">
          <img src="/logo3duo.png" alt="3Duo" className="h-9 w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === l.to ? "bg-foreground text-white" : "text-foreground/70 hover:text-foreground hover:bg-muted"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/*
          Grouped so the cart sits beside the menu. Loose, they were three
          separate children of a justify-between row, which on a phone - where
          the desktop links and the call to action are hidden - parked the cart
          on its own in the middle of the bar.
        */}
        <div className="flex items-center gap-1 md:gap-2 shrink-0">
          <button
            data-testid="cta-orcamento-nav"
            onClick={() => nav("/contactos")}
            className="hidden md:inline-flex items-center rounded-full brand-gradient text-white text-sm font-semibold px-5 py-2.5 transition-transform hover:scale-105 active:scale-95"
          >
            Pedir Orçamento
          </button>

          <button data-testid="cart-button" onClick={() => setCartOpen(true)} className="relative p-2.5 rounded-full hover:bg-muted transition-colors" aria-label="Pedido de orçamento">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full brand-gradient text-white text-[10px] font-bold">{count}</span>
            )}
          </button>

          <button data-testid="menu-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-auto max-w-6xl mt-2 rounded-3xl bg-white shadow-xl border border-border p-4 flex flex-col gap-1"
          >
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-4 py-3 rounded-2xl font-medium hover:bg-muted">{l.label}</Link>
            ))}
            <button onClick={() => nav("/contactos")} className="mt-1 rounded-2xl brand-gradient text-white font-semibold py-3">Pedir Orçamento</button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
